import { useCallback, useEffect, useState } from 'react';

import { useTrackTeamInviteCtaClicked } from '@air/analytics';
import { useAirModal } from '@air/provider-modal';

import { InviteMembersModal } from '~/components/Modals/InviteMembers/InviteMembersModal';
import { useTeamInviteNudgeEligibility } from '~/hooks/useTeamInviteNudgeEligibility';
import {
  saveSideNavTeamInviteNudgeDismissed,
  TEAM_INVITE_NUDGE_INVITE_TRIGGERED_EVENT,
  wasSideNavTeamInviteNudgeDismissed,
} from '~/utils/teamInviteNudge';

const getInviteTriggeredWorkspaceId = (event: Event) => {
  if (!(event instanceof CustomEvent)) {
    return undefined;
  }

  const detail: unknown = event.detail;

  if (!detail || typeof detail !== 'object' || !('workspaceId' in detail)) {
    return undefined;
  }

  return typeof detail.workspaceId === 'string' ? detail.workspaceId : undefined;
};

export const useShowSideNavTeamInviteNudge = () => {
  const { isTeamInviteNudgeEligible, workspaceId, workspaceName } = useTeamInviteNudgeEligibility();
  const { trackTeamInviteCtaClicked } = useTrackTeamInviteCtaClicked();
  const [showInviteMembersModal] = useAirModal(InviteMembersModal);
  const [dismissedWorkspaceId, setDismissedWorkspaceId] = useState<string>();
  const [inviteTriggeredWorkspaceId, setInviteTriggeredWorkspaceId] = useState<string>();

  const isSideNavTeamInviteNudgeDismissed =
    !!workspaceId && (dismissedWorkspaceId === workspaceId || wasSideNavTeamInviteNudgeDismissed({ workspaceId }));
  const wasInviteTriggeredForWorkspace = workspaceId === inviteTriggeredWorkspaceId;

  useEffect(() => {
    const handleInviteTriggered = (event: Event) => {
      const triggeredWorkspaceId = getInviteTriggeredWorkspaceId(event);

      if (triggeredWorkspaceId === workspaceId) {
        setInviteTriggeredWorkspaceId(triggeredWorkspaceId);
      }
    };

    window.addEventListener(TEAM_INVITE_NUDGE_INVITE_TRIGGERED_EVENT, handleInviteTriggered);

    return () => window.removeEventListener(TEAM_INVITE_NUDGE_INVITE_TRIGGERED_EVENT, handleInviteTriggered);
  }, [workspaceId]);

  const dismissSideNavTeamInviteNudge = useCallback(() => {
    if (workspaceId) {
      saveSideNavTeamInviteNudgeDismissed({ workspaceId });
    }

    setDismissedWorkspaceId(workspaceId);
  }, [workspaceId]);

  const openSideNavTeamInviteNudgeModal = useCallback(() => {
    trackTeamInviteCtaClicked({ location: 'side_nav' });
    showInviteMembersModal({ location: 'side-nav' });
  }, [showInviteMembersModal, trackTeamInviteCtaClicked]);

  return {
    dismissSideNavTeamInviteNudge,
    isSideNavTeamInviteNudgeVisible:
      isTeamInviteNudgeEligible && !isSideNavTeamInviteNudgeDismissed && !wasInviteTriggeredForWorkspace,
    openSideNavTeamInviteNudgeModal,
    workspaceName,
  };
};
